import React, { useMemo, useRef } from 'react';

import { ProductLink, Venue, type App } from 'generated/graphql';

import { AppsList } from 'components/AppsList/AppsList';
import EditableText from 'components/Inputs/EditableText';
import { Droppable } from 'react-beautiful-dnd';

import Image from 'components/Image/Image';
import Checkbox from 'components/Inputs/Checkbox';

import { formatPrice } from 'utils';

import AvailabilityPill from 'components/AvailabilityPill/AvailabilityPill';
import { useQuery } from '@apollo/client';
import { ProductLinksQuery } from 'queries/Product';
import useFeatureFlagsQuery from 'common/Hooks/UseFeatureFlagsQuery';
import DraggableOption from './Option/DraggableOption';
import styles from './OptionList.module.scss';

const AVAILABILITY_BY_VARIANT_FEATURE_FLAG = 'availabilityByVariantEnabled';

export type PricesRow = {
  id: string;
  imageAlt?: string;
  prices: (number | undefined | null)[];
  disabled?: boolean;
  image?: string | undefined | null;
};

type Surcharge = {
  amount: number;
  type: string;
};

export type Row = {
  apps?: App[];
  prices: PricesRow[];
  label?: string;
  id?: string;
  disabled?: boolean;
  availability?: string;
  surcharge?: Surcharge | null;
  _typename: string;
};

type CallBack = {
  label: string;
  handleCallback: () => void;
};

type JsxOrString = string | JSX.Element;

interface Props {
  options: Row[];
  addCallback?: CallBack;
  handleChange: (index: number, id: string, value: string) => void;
  handleNameChange?: (index: number, value: string) => void;
  handleToggle?: (index: number, isChecked?: boolean) => void;
  dndId?: string;
  columnHeadings?: {
    key: string;
    value: JsxOrString;
  }[];
  isDragDisabled?: boolean;
  toggleDragOnEdits?: (type: 'focus' | 'enter' | 'blur') => void;
  parentId?: string;
  currentVenue?: Venue | null;
  posPrice?: number;
}

const OptionList: React.FC<Props> = ({
  options,
  addCallback,
  handleChange,
  handleNameChange,
  handleToggle,
  dndId,
  columnHeadings,
  isDragDisabled = false,
  toggleDragOnEdits,
  parentId,
  currentVenue,
  posPrice
}) => {
  const [data, loading, error] = useFeatureFlagsQuery([AVAILABILITY_BY_VARIANT_FEATURE_FLAG]);
  const isAvailabilityByVariantEnabled = useMemo(() => {
    if (loading || error || !data) {
      return false;
    }

    const availabilityByVariantFeatureFlag = data[AVAILABILITY_BY_VARIANT_FEATURE_FLAG];
    return availabilityByVariantFeatureFlag ? availabilityByVariantFeatureFlag.isEnabled : false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const headingsRef = useRef<HTMLLIElement | null>(null);

  const shouldDisplayInGrid = options.length && options[0].prices.length > 3;

  const headingGridStyle = ((): string | undefined => {
    if (!columnHeadings) {
      return undefined;
    }

    if (shouldDisplayInGrid) {
      return `1fr auto`;
    }

    let response = '1fr';

    let hasMultiPrice = false;

    options.forEach((option) => {
      option.prices.forEach(({ prices }) => {
        if (prices.length > 1 && prices[0] !== prices[prices.length - 1]) {
          hasMultiPrice = true;
        }
      });
    });

    if (options.length) {
      options[0].prices.forEach(() => {
        response += ` ${hasMultiPrice ? 110 : 80}px`;
      });
    }

    if (options[0]?.availability) {
      response += ` ${95}px`;
    }

    return response;
  })();

  const appIds = useMemo(() => options.map((option) => option.apps?.[0]?.id), [options]);

  const menuSurcharges = useMemo(() => {
    return currentVenue?.menus?.filter((menu) => menu.surcharge) ?? [];
  }, [currentVenue]);

  const optionsWithSurcharge = useMemo(() => {
    return options.map((option) => {
      const matchingSurcharge = menuSurcharges.reduce((acc, menu) => {
        const hasMatchingApp = option.apps?.[0]?.id && menu.apps.some((app) => app.id === option.apps?.[0].id);
        return hasMatchingApp ? menu.surcharge : acc;
      }, option.surcharge);
      return {
        ...option,
        surcharge: matchingSurcharge ?? null
      };
    });
  }, [options, menuSurcharges]);

  const itemContainsSurcharge = useMemo(
    () => optionsWithSurcharge.some((option) => option.surcharge && option.surcharge.amount > 0),
    [optionsWithSurcharge]
  );

  const { loading: productLinksLoading, data: productLinksData } = useQuery<
    { productLinks: ProductLink[] },
    { locationId?: string; posId?: string; appIds?: string[] }
  >(ProductLinksQuery, {
    variables: {
      posId: parentId,
      appIds: appIds.filter((id): id is string => id !== undefined && id !== '1') || [''],
      locationId: currentVenue?.hashedId
    },
    skip: !itemContainsSurcharge,
    fetchPolicy: 'network-only'
  });

  const listItemsJsx = optionsWithSurcharge.map(
    ({ apps, label = '', prices, id, disabled: optionDisabled, surcharge, _typename }, index) => {
      const isProduct = _typename === 'Product';
      const pricesInner = prices.map(({ id, prices, image, imageAlt, disabled }) => {
        let price = prices[0];
        if (disabled && prices.length === 1 && typeof price !== 'number') {
          return <span key={id} />;
        }

        // Base Price comes from the product app Link
        const productLink = productLinksData?.productLinks?.find((productLink) => productLink.appId === apps?.[0]?.id);
        const basePrice = !productLink?.unitPrice && !posPrice ? '' : formatPrice(productLink?.unitPrice || posPrice);

        // Update the visual price displayed if a surcharge exists when adding a new product
        if (surcharge && surcharge.amount > 0 && isProduct && !productLink && !productLinksLoading) {
          price = (price || 0) * (1 + surcharge.amount / 100);
        }

        const formattedPrice = formatPrice(price);
        const sortedPrices = prices.slice().sort((a, b) => (a ?? 0) - (b ?? 0));

        let text =
          prices.length > 1 && price !== prices[prices.length - 1]
            ? `${formatPrice(sortedPrices[0])} - ${formatPrice(sortedPrices[sortedPrices.length - 1])}`
            : formattedPrice;

        if (surcharge && surcharge.amount > 0 && isProduct) {
          text = `→ ${text}`;
        }

        const isPosRow = id === '-1';

        if (productLinksLoading) {
          return null;
        }

        return (
          <span
            data-testid="option-list-price-input"
            key={id}
            className={`${disabled ? styles.Disabled : ''} ${styles.Price}`}
          >
            {itemContainsSurcharge &&
            surcharge &&
            surcharge.amount > 0 &&
            !isPosRow &&
            (isProduct || (!isProduct && optionDisabled === false)) ? (
              <>
                {isProduct ? <span className={styles.BasePrice}>{basePrice} </span> : null}
                <div className={styles.Surcharge}>
                  <span data-testid="item-surcharge">{isProduct ? `+${surcharge?.amount.toFixed(0)}%` : '%'}</span>
                </div>
              </>
            ) : null}
            {shouldDisplayInGrid ? <Image fallbackUrl="/Images/NoApp.png" src={image} alt={imageAlt || ''} /> : null}
            {!(surcharge && surcharge.amount > 0) && !disabled ? (
              <EditableText
                text={text}
                onSubmit={(value) => {
                  handleChange(index, id, value);
                }}
                disabled={optionDisabled}
                onStateChange={toggleDragOnEdits}
                hasInternalState
                testId={`${parentId || ''}-${id}-edit-price`}
                isPrice
              />
            ) : (
              <span className={styles.PriceText}>{text}</span>
            )}
          </span>
        );
      });

      return {
        key: `${id} -${apps ? apps.map((app) => app.id) : 'no-app'} `,
        id,
        optionDisabled,
        content: (
          <>
            {apps && <AppsList apps={apps} />}
            <span aria-labelledby={`${id}-label`} className={styles.RowName}>
              {handleToggle && !itemContainsSurcharge ? (
                <button
                  data-testid={`${parentId || ''}-${id}-is-enabled`}
                  aria-label={`${optionDisabled ? 'enable' : 'disable'} ${label}`}
                  type="button"
                  onClick={() => handleToggle(index, optionDisabled)}
                >
                  <Checkbox isChecked={!optionDisabled} />
                </button>
              ) : null}
              {handleNameChange ? (
                <EditableText
                  text={label}
                  onSubmit={(value) => {
                    handleNameChange(index, value);
                  }}
                  disabled={optionDisabled}
                  onStateChange={toggleDragOnEdits}
                  hasInternalState
                  labelId={`${id}-label`}
                  testId={`${parentId || ''}-${id}-edit-name`}
                />
              ) : (
                <span id={`${id}-label`}>{label}</span>
              )}
            </span>
            {shouldDisplayInGrid ? <div className={styles.RowGrid}>{pricesInner}</div> : pricesInner}
            {isAvailabilityByVariantEnabled &&
              options[index].availability &&
              options[index].availability !== 'unknown' && (
                <span>
                  <AvailabilityPill availability={options[index].availability as string} editable={false} />
                </span>
              )}
          </>
        )
      };
    }
  );

  return (
    <ul className={styles.OptionList} data-testid="option-list">
      {columnHeadings && columnHeadings.length && !shouldDisplayInGrid ? (
        <li
          style={{
            gridTemplateColumns: headingGridStyle
          }}
          ref={headingsRef}
          className={styles.HeadingRow}
        >
          {columnHeadings.map(({ key, value }) => (
            <span style={{ left: '-15px' }} data-testid="option-list-column-heading" key={key}>
              {value}
            </span>
          ))}
        </li>
      ) : null}
      {dndId ? (
        <Droppable droppableId={dndId} type={`OPTION - ${dndId} `}>
          {(provided) => (
            <div className={styles.OptionGroups} ref={provided.innerRef}>
              {listItemsJsx.map(({ key, id, content, optionDisabled }, index) => (
                <DraggableOption
                  key={key}
                  id={id}
                  content={content}
                  index={index}
                  headingGridStyle={headingGridStyle}
                  shouldDisplayInGrid={shouldDisplayInGrid}
                  isDragDisabled={optionDisabled || isDragDisabled}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      ) : (
        listItemsJsx.map(({ key, content }) => {
          return <li key={key}>{content}</li>;
        })
      )}
      {addCallback && (
        <li className={styles.AddOption}>
          <button type="button" onClick={addCallback.handleCallback}>
            <i className="mm-plus" />
            <span>{addCallback.label}</span>
          </button>
        </li>
      )}
    </ul>
  );
};

export default OptionList;
